import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, selectMembers, selectLinkedPairs } from '@/utils/store';
import { formatDate, CATEGORY_ICONS } from '@/utils/format';
import { useCurrency } from '@/hooks/useCurrency';
import { Button } from '@/components/ui';
import PageHeader from '@/components/shared/PageHeader';
import AddTransactionModal from '@/components/shared/AddTransactionModal';
import BulkEditModal from '@/components/shared/BulkEditModal';
import ScanReceiptModal from '@/components/shared/ScanReceiptModal';
import { Plus, Search, Trash2, CheckSquare, X, Pencil, Users, List, LayoutGrid, Camera, Link2, Unlink, Copy } from 'lucide-react';
import { startOfMonth, subMonths, isWithinInterval, endOfMonth } from 'date-fns';

function TransactionCard({ tx, member, members: txMembers = [], selectMode, isSelected, onToggle, onEdit, onDelete, onDuplicate, compact }) {
  const { t } = useTranslation();
  const { format: formatCurrency } = useCurrency();

  return (
    <div
      onClick={selectMode ? onToggle : onEdit}
      className={`card flex items-center gap-3 ${compact ? 'p-3' : 'p-4'} group transition-all cursor-pointer border-l-4 ${
        tx.type === 'income' ? 'border-l-success-500' : 'border-l-danger-500'
      } ${
        isSelected
          ? 'ring-2 ring-brand-500 bg-brand-50 dark:bg-brand-500/10'
          : ''
      }`}
    >
      {selectMode && (
        <div
          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
            isSelected
              ? 'border-brand-500 bg-brand-500'
              : 'border-gray-300 dark:border-gray-600'
          }`}
        >
          {isSelected && (
            <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
              <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
      )}

      <div className={`${compact ? 'w-8 h-8' : 'w-10 h-10'} rounded-xl flex items-center justify-center ${compact ? 'text-base' : 'text-lg'} ${
        tx.type === 'income'
          ? 'bg-success-50 dark:bg-success-500/10'
          : 'bg-danger-50 dark:bg-danger-500/10'
      }`}>
        {CATEGORY_ICONS[tx.category]}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-900 dark:text-white truncate`}>
          {tx.description}
        </div>
        <div className="text-xs text-gray-500 flex items-center gap-1.5">
          <span>{t(`categories.${tx.category}`)} · {formatDate(tx.date)}</span>
          {txMembers.length > 0 && txMembers.map((m) => (
            m.emoji ? (
              <span
                key={m.id}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium"
                style={{ backgroundColor: m.color + '20', color: m.color }}
              >
                {m.emoji} {m.name}
              </span>
            ) : (
              <span
                key={m.id}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium text-white"
                style={{ backgroundColor: m.color }}
              >
                {m.initials}
              </span>
            )
          ))}
        </div>
      </div>
      <span className={`text-sm font-bold ${tx.type === 'income' ? 'text-success-500' : 'text-danger-500'}`}>
        {tx.type === 'income' ? '+' : '-'}{formatCurrency(tx.amount)}
      </span>
      {!selectMode && !compact && (
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-all">
          <button
            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
            aria-label={t('transactions.duplicate')}
          >
            <Copy size={14} className="text-gray-400 hover:text-brand-500" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 rounded-lg hover:bg-danger-50 dark:hover:bg-danger-500/10 transition-all"
            aria-label={t('common.delete')}
          >
            <Trash2 size={14} className="text-danger-500" />
          </button>
        </div>
      )}
    </div>
  );
}

function LinkedView({ linkedPairs, transactions, linkingTx, setLinkingTx, linkTransactions, unlinkTransaction, formatCurrency, t }) {
  const linkedIds = new Set();
  linkedPairs.forEach(({ income, expense }) => {
    linkedIds.add(income.id);
    linkedIds.add(expense.id);
  });
  const unlinked = transactions.filter((tx) => !linkedIds.has(tx.id) && !tx.linkedTo);

  const handleLink = (targetId) => {
    if (!linkingTx) return;
    linkTransactions(linkingTx.id, targetId);
    setLinkingTx(null);
  };

  const candidates = linkingTx
    ? unlinked.filter((tx) => tx.type !== linkingTx.type)
    : [];

  return (
    <div className="space-y-4">
      {/* Linked pairs */}
      {linkedPairs.length > 0 && (
        <div className="space-y-3">
          {linkedPairs.map(({ income, expense, coverage }) => (
            <div key={`${income.id}-${expense.id}`} className="card p-4 space-y-3">
              {/* Income row */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-success-50 dark:bg-success-500/10 flex items-center justify-center text-sm">
                  {CATEGORY_ICONS[income.category] || '💰'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{income.description}</div>
                  <div className="text-xs text-gray-500">{t(`categories.${income.category}`)} · {formatDate(income.date)}</div>
                </div>
                <span className="text-sm font-bold text-success-500">+{formatCurrency(income.amount)}</span>
              </div>
              {/* Arrow */}
              <div className="flex justify-center">
                <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <Link2 size={12} className="text-gray-400" />
                </div>
              </div>
              {/* Expense row */}
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-danger-50 dark:bg-danger-500/10 flex items-center justify-center text-sm">
                  {CATEGORY_ICONS[expense.category] || '📌'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{expense.description}</div>
                  <div className="text-xs text-gray-500">{t(`categories.${expense.category}`)} · {formatDate(expense.date)}</div>
                </div>
                <span className="text-sm font-bold text-danger-500">-{formatCurrency(expense.amount)}</span>
              </div>
              {/* Coverage bar */}
              <div className="border-t border-gray-100 dark:border-gray-700 pt-3 flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">{t('transactions.linked.coverage')}</span>
                    <span className={`text-xs font-bold ${coverage > 100 ? 'text-danger-500' : coverage > 75 ? 'text-warning-500' : 'text-success-500'}`}>
                      {coverage}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${coverage > 100 ? 'bg-danger-500' : coverage > 75 ? 'bg-warning-500' : 'bg-success-500'}`}
                      style={{ width: `${Math.min(coverage, 100)}%` }}
                    />
                  </div>
                </div>
                <button
                  onClick={() => unlinkTransaction(income.id)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-500/10 transition-colors"
                  title={t('transactions.linked.unlink')}
                >
                  <Unlink size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Unlinked transactions */}
      {unlinked.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            {t('transactions.linked.unlinked')} ({unlinked.length})
          </h3>
          <div className="space-y-2">
            {unlinked.map((tx) => (
              <div key={tx.id}>
                <div className="card p-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-sm">
                    {CATEGORY_ICONS[tx.category] || '📌'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{tx.description}</div>
                    <div className="text-xs text-gray-500">{t(`categories.${tx.category}`)} · {formatDate(tx.date)}</div>
                  </div>
                  <span className={`text-sm font-bold ${tx.type === 'income' ? 'text-success-500' : 'text-danger-500'}`}>
                    {tx.type === 'income' ? '+' : '-'}{formatCurrency(tx.amount)}
                  </span>
                  <button
                    onClick={() => setLinkingTx(linkingTx?.id === tx.id ? null : tx)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                      linkingTx?.id === tx.id
                        ? 'bg-brand-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-brand-50 dark:hover:bg-brand-500/10 hover:text-brand-600'
                    }`}
                  >
                    <Link2 size={12} className="inline mr-1" />
                    {t('transactions.linked.linkTo')}
                  </button>
                </div>
                {/* Candidate pills when this tx is being linked */}
                {linkingTx?.id === tx.id && candidates.length > 0 && (
                  <div className="mt-2 ml-11 flex flex-wrap gap-1.5">
                    <span className="text-xs text-gray-400 w-full mb-1">
                      {tx.type === 'income' ? t('transactions.linked.selectExpense') : t('transactions.linked.selectIncome')}
                    </span>
                    {candidates.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => handleLink(c.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          c.type === 'income'
                            ? 'border-success-200 dark:border-success-500/30 bg-success-50 dark:bg-success-500/10 text-success-700 dark:text-success-400 hover:border-success-400'
                            : 'border-danger-200 dark:border-danger-500/30 bg-danger-50 dark:bg-danger-500/10 text-danger-700 dark:text-danger-400 hover:border-danger-400'
                        }`}
                      >
                        {c.description} ({c.type === 'income' ? '+' : '-'}{formatCurrency(c.amount)})
                      </button>
                    ))}
                  </div>
                )}
                {linkingTx?.id === tx.id && candidates.length === 0 && (
                  <div className="mt-2 ml-11 text-xs text-gray-400">
                    {tx.type === 'income' ? t('transactions.linked.selectExpense') : t('transactions.linked.selectIncome')} — {t('transactions.linked.noLinks')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {linkedPairs.length === 0 && unlinked.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          {t('transactions.linked.noLinks')}
        </div>
      )}
    </div>
  );
}

export default function Transactions() {
  const { t } = useTranslation();
  const { format: formatCurrency } = useCurrency();
  const transactions = useStore((s) => s.transactions);
  const addTransaction = useStore((s) => s.addTransaction);
  const removeTransaction = useStore((s) => s.removeTransaction);
  const bulkRemoveTransactions = useStore((s) => s.bulkRemoveTransactions);
  const linkTransactions = useStore((s) => s.linkTransactions);
  const unlinkTransaction = useStore((s) => s.unlinkTransaction);
  const members = useStore(selectMembers);
  const linkedPairs = useStore(selectLinkedPairs);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [monthFilter, setMonthFilter] = useState('current');
  const [groupBy, setGroupBy] = useState('none');
  const [viewMode, setViewMode] = useState('list');
  const [scanOpen, setScanOpen] = useState(false);
  const [linkingTx, setLinkingTx] = useState(null);

  // Selection mode
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [showBulkEdit, setShowBulkEdit] = useState(false);

  const now = useMemo(() => new Date(), []);
  const currentMonthStart = useMemo(() => startOfMonth(now), [now]);
  const previousMonthStart = useMemo(() => startOfMonth(subMonths(now, 1)), [now]);

  const filtered = useMemo(() => {
    let result = transactions
      .filter((tx) => {
        if (viewMode === 'kanban') return true;
        if (filter === 'income') return tx.type === 'income';
        if (filter === 'expense') return tx.type === 'expense';
        return true;
      })
      .filter((tx) =>
        tx.description.toLowerCase().includes(search.toLowerCase())
      );

    // Month filter
    result = result.filter((tx) => {
      if (monthFilter === 'all') return true;
      const d = new Date(tx.date + 'T00:00:00');
      const ref = monthFilter === 'current' ? currentMonthStart : previousMonthStart;
      return isWithinInterval(d, { start: ref, end: endOfMonth(ref) });
    });

    return result;
  }, [transactions, filter, search, monthFilter, viewMode, currentMonthStart, previousMonthStart]);

  // Group by member
  const memberGroups = useMemo(() => {
    if (groupBy !== 'member' || viewMode === 'kanban') return null;
    const groups = new Map();
    for (const tx of filtered) {
      const ids = tx.memberIds?.length > 0 ? tx.memberIds : ['__none__'];
      for (const id of ids) {
        if (!groups.has(id)) {
          const member = id !== '__none__' ? members.find((m) => m.id === id) : null;
          groups.set(id, { member, transactions: [], total: 0 });
        }
        const g = groups.get(id);
        if (!g.transactions.some((t) => t.id === tx.id)) {
          g.transactions.push(tx);
          g.total += tx.type === 'income' ? tx.amount : -tx.amount;
        }
      }
    }
    return [...groups.values()];
  }, [filtered, groupBy, viewMode, members]);

  // Kanban splits
  const kanbanData = useMemo(() => {
    if (viewMode !== 'kanban') return null;
    const income = filtered.filter((tx) => tx.type === 'income');
    const expense = filtered.filter((tx) => tx.type === 'expense');
    const incomeTotal = income.reduce((s, tx) => s + tx.amount, 0);
    const expenseTotal = expense.reduce((s, tx) => s + tx.amount, 0);
    return { income, expense, incomeTotal, expenseTotal };
  }, [filtered, viewMode]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(filtered.map((tx) => tx.id)));
  };

  const deselectAll = () => {
    setSelected(new Set());
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const handleBulkDelete = () => {
    if (window.confirm(t('transactions.bulkDeleteConfirm', { count: selected.size }))) {
      bulkRemoveTransactions([...selected]);
      exitSelectMode();
    }
  };

  const handleBulkEditClose = () => {
    setShowBulkEdit(false);
    exitSelectMode();
  };

  const handleDuplicate = (tx) => {
    const { id, linkedTo, ...rest } = tx;
    addTransaction({ ...rest, date: new Date().toISOString().split('T')[0] });
  };

  const renderCard = (tx) => {
    const isSelected = selected.has(tx.id);
    const txMembers = tx.memberIds?.length > 0
      ? tx.memberIds.map((id) => members.find((m) => m.id === id)).filter(Boolean)
      : [];
    return (
      <TransactionCard
        key={tx.id}
        tx={tx}
        member={txMembers[0] || null}
        members={txMembers}
        selectMode={selectMode}
        isSelected={isSelected}
        onToggle={() => toggleSelect(tx.id)}
        onEdit={() => setEditingTx(tx)}
        onDelete={() => removeTransaction(tx.id)}
        onDuplicate={() => handleDuplicate(tx)}
        compact={viewMode === 'kanban'}
      />
    );
  };

  return (
    <div className="space-y-4 animate-fade-in pb-24">
      <PageHeader
        title={selectMode
          ? t('transactions.selected', { count: selected.size })
          : t('transactions.title')
        }
        hint={!selectMode ? t('common.hints.transactions') : undefined}
        action={
          selectMode ? (
            <Button size="sm" variant="ghost" icon={X} onClick={exitSelectMode}>
              {t('transactions.cancel')}
            </Button>
          ) : (
            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* View toggle */}
              <div className="flex bg-gray-100 dark:bg-gray-800 rounded-xl p-0.5">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded-lg transition-colors ${
                    viewMode === 'list'
                      ? 'bg-white dark:bg-gray-700 shadow-sm text-brand-600 dark:text-brand-400'
                      : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                  }`}
                  title={t('transactions.viewList')}
                >
                  <List size={16} />
                </button>
                <button
                  onClick={() => setViewMode('kanban')}
                  className={`p-1.5 rounded-lg transition-colors ${
                    viewMode === 'kanban'
                      ? 'bg-white dark:bg-gray-700 shadow-sm text-brand-600 dark:text-brand-400'
                      : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                  }`}
                  title={t('transactions.viewKanban')}
                >
                  <LayoutGrid size={16} />
                </button>
                <button
                  onClick={() => setViewMode('linked')}
                  className={`p-1.5 rounded-lg transition-colors ${
                    viewMode === 'linked'
                      ? 'bg-white dark:bg-gray-700 shadow-sm text-brand-600 dark:text-brand-400'
                      : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                  }`}
                  title={t('transactions.viewLinked')}
                >
                  <Link2 size={16} />
                </button>
              </div>
              <button
                onClick={() => setScanOpen(true)}
                className="p-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-brand-600 transition-colors sm:hidden"
                title={t('scan.scanReceipt')}
              >
                <Camera size={16} />
              </button>
              <button
                onClick={() => setSelectMode(true)}
                className="p-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-brand-600 transition-colors sm:hidden"
                title={t('transactions.select')}
              >
                <CheckSquare size={16} />
              </button>
              <div className="hidden sm:flex items-center gap-2">
                <Button size="sm" variant="ghost" icon={Camera} onClick={() => setScanOpen(true)}>
                  {t('scan.scanReceipt')}
                </Button>
                <Button size="sm" variant="ghost" icon={CheckSquare} onClick={() => setSelectMode(true)}>
                  {t('transactions.select')}
                </Button>
              </div>
              <Button size="sm" icon={Plus} onClick={() => setShowModal(true)}>
                <span className="hidden sm:inline">{t('transactions.addNew')}</span>
              </Button>
            </div>
          )
        }
      />

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          className="input pl-10"
          placeholder={t('transactions.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Filter tabs + month pills + group toggle */}
      <div className="space-y-2">
        {/* Type filter — hidden in kanban */}
        {viewMode !== 'kanban' && (
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className="flex gap-1.5 sm:gap-2 flex-1 min-w-0">
              {['all', 'income', 'expense'].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                    filter === f
                      ? 'bg-brand-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {f === 'all' ? t('transactions.all') :
                   f === 'income' ? t('transactions.incomes') :
                   t('transactions.expenses')}
                </button>
              ))}
            </div>
            {/* Group by member toggle */}
            <button
              onClick={() => setGroupBy(groupBy === 'none' ? 'member' : 'none')}
              className={`p-2 rounded-xl transition-colors flex-shrink-0 ${
                groupBy === 'member'
                  ? 'bg-brand-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
              }`}
              title={t('transactions.groupByMember')}
            >
              <Users size={16} />
            </button>
          </div>
        )}

        {/* Month filter pills */}
        <div className="flex gap-1.5 sm:gap-2">
          {['current', 'previous', 'all'].map((m) => (
            <button
              key={m}
              onClick={() => setMonthFilter(m)}
              className={`px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                monthFilter === m
                  ? 'bg-brand-100 dark:bg-brand-500/20 text-brand-700 dark:text-brand-300'
                  : 'bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400'
              }`}
            >
              {m === 'current' ? t('transactions.monthCurrent') :
               m === 'previous' ? t('transactions.monthPrevious') :
               t('transactions.monthAll')}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {viewMode === 'linked' ? (
        /* Linked pairs view */
        <LinkedView
          linkedPairs={linkedPairs}
          transactions={filtered}
          linkingTx={linkingTx}
          setLinkingTx={setLinkingTx}
          linkTransactions={linkTransactions}
          unlinkTransaction={unlinkTransaction}
          formatCurrency={formatCurrency}
          t={t}
        />
      ) : viewMode === 'kanban' && kanbanData ? (
        /* Kanban view */
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Income column */}
          <div className="space-y-2">
            <div className="sticky top-0 z-10 bg-success-50 dark:bg-success-500/10 rounded-xl p-3">
              <div className="text-xs font-semibold text-success-600 dark:text-success-400">{t('transactions.incomeColumn')}</div>
              <div className="text-base sm:text-lg font-bold text-success-700 dark:text-success-300">{formatCurrency(kanbanData.incomeTotal)}</div>
              <div className="text-[10px] text-success-500">{kanbanData.income.length} {t('transactions.incomes').toLowerCase()}</div>
            </div>
            {kanbanData.income.map(renderCard)}
          </div>
          {/* Expense column */}
          <div className="space-y-2">
            <div className="sticky top-0 z-10 bg-danger-50 dark:bg-danger-500/10 rounded-xl p-3">
              <div className="text-xs font-semibold text-danger-600 dark:text-danger-400">{t('transactions.expenseColumn')}</div>
              <div className="text-base sm:text-lg font-bold text-danger-700 dark:text-danger-300">{formatCurrency(kanbanData.expenseTotal)}</div>
              <div className="text-[10px] text-danger-500">{kanbanData.expense.length} {t('transactions.expenses').toLowerCase()}</div>
            </div>
            {kanbanData.expense.map(renderCard)}
          </div>
        </div>
      ) : memberGroups ? (
        /* Grouped by member view */
        <div className="space-y-4">
          {memberGroups.map(({ member, transactions: txs, total }) => (
            <div key={member?.id || '__none__'}>
              <div className="flex items-center gap-2 py-2 px-1">
                {member ? (
                  <span
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: member.color }}
                  >
                    {member.initials} {member.name}
                  </span>
                ) : (
                  <span className="text-sm font-medium text-gray-400 dark:text-gray-500">
                    {t('transactions.unassigned')}
                  </span>
                )}
                <span className={`text-sm font-bold ${total >= 0 ? 'text-success-500' : 'text-danger-500'}`}>
                  {total >= 0 ? '+' : ''}{formatCurrency(Math.abs(total))}
                </span>
                <span className="text-xs text-gray-400">({txs.length})</span>
              </div>
              <div className="space-y-2">
                {txs.map(renderCard)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Flat list view */
        <div className="space-y-2">
          {filtered.map(renderCard)}
        </div>
      )}

      {filtered.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          {t('transactions.noTransactions')}
        </div>
      )}

      {/* Sticky bulk action bar */}
      {selectMode && selected.size > 0 && (
        <div className="fixed bottom-20 left-0 right-0 z-40 px-4">
          <div className="max-w-lg mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-2.5 sm:p-3 flex items-center gap-1.5 sm:gap-2">
            <button
              onClick={selected.size === filtered.length ? deselectAll : selectAll}
              className="text-xs font-medium text-brand-600 dark:text-brand-400 px-2 sm:px-3 py-1.5 rounded-lg hover:bg-brand-50 dark:hover:bg-brand-500/10 transition-colors whitespace-nowrap"
            >
              {selected.size === filtered.length
                ? t('transactions.deselectAll')
                : t('transactions.selectAll')}
            </button>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="ghost"
              icon={Pencil}
              onClick={() => setShowBulkEdit(true)}
            >
              <span className="hidden sm:inline">{t('transactions.bulkEdit')}</span>
            </Button>
            <Button
              size="sm"
              variant="danger"
              icon={Trash2}
              onClick={handleBulkDelete}
            >
              <span className="hidden sm:inline">{t('transactions.bulkDelete')}</span>
            </Button>
          </div>
        </div>
      )}

      <AddTransactionModal open={showModal} onClose={() => setShowModal(false)} onScanReceipt={() => setScanOpen(true)} />
      <AddTransactionModal
        open={!!editingTx}
        onClose={() => setEditingTx(null)}
        transaction={editingTx}
      />
      <BulkEditModal
        open={showBulkEdit}
        onClose={handleBulkEditClose}
        transactionIds={[...selected]}
      />
      <ScanReceiptModal open={scanOpen} onClose={() => setScanOpen(false)} />
    </div>
  );
}
